import useSWR from 'swr';
import { API_GET_LEAD_TYPES } from '../api/configurationScreenServices';

/**
 * Hook returning the lead types catalogue and whether lead-types are
 * enabled for the tenant. Replaces `useSelector((s) => s.leadControls)`
 * reads of `leadTypes` / `leadTypesEnabled` and the `getLeadTypes` thunk.
 *
 * Mirrors the old leadControlsSlice thunk's normalisation: if the backend
 * returns an empty list, lead-types is considered disabled and a sentinel
 * "default" lead-type is returned so consumer code can assume at least one.
 */

const DEFAULT_LEAD_TYPE = {
  LeadTypeId: 'default',
  Name: 'Default',
  FieldConfiguration: null
};

export const leadTypesKey = 'leadTypes';

const fetcher = async () => {
  const response = await API_GET_LEAD_TYPES();
  if (response?.status !== 200) {
    throw new Error(response?.data?.message || 'Failed to load lead types');
  }
  const data = response.data || [];
  const isEnabled = data.length !== 0;
  const rawLeadTypes = isEnabled ? data : [DEFAULT_LEAD_TYPE];
  return {
    leadTypesEnabled: isEnabled,
    // Raw list with real GUIDs — for consumers that need the actual
    // LeadTypeId (associations, widget essentials). Mirrors the old
    // repo's `leadTypesList`.
    leadTypesList: rawLeadTypes,
    // Backend contract: the DEFAULT lead type is addressed by the literal
    // string "default" in create/update payloads (leadTypeId: "default"),
    // not by its GUID. Mirrors the old repo's leadControlsSlice mapping.
    leadTypes: rawLeadTypes.map((leadType) => ({
      ...leadType,
      LeadTypeId: leadType?.IsDefault ? 'default' : leadType.LeadTypeId
    }))
  };
};

export function useLeadTypes() {
  const { data, error, isLoading, mutate } = useSWR(leadTypesKey, fetcher, {
    revalidateOnFocus: false,
    revalidateIfStale: false,
    revalidateOnReconnect: false
  });

  return {
    leadTypesEnabled: data?.leadTypesEnabled ?? false,
    leadTypes: data?.leadTypes ?? [],
    leadTypesList: data?.leadTypesList ?? [],
    loading: isLoading,
    error,
    refetch: mutate
  };
}
